import React, { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { PrjSchedule } from 'models/setup/PrjSchedule';
import {
  CalBoard,
  IncompleteAlertBadge,
  DelayedTagBadge,
  CalEventTitleText,
  TimelineGridContainer,
  TimelineHeaderCol,
  TimelineDayHeaderCell,
  TimelineDayNameText,
  TimelineDayDateText,
  TimelineAllDayLabel,
  TimelineAllDayContent,
  TimelineAllDayChip,
  TimelineTimeAxisCol,
  TimelineTimeAxisGrid,
  TimelineHourLabelCell,
  TimelineDayColumnArea,
  TimelineDragIndicatorBox,
  TimelineDragIndicatorBadge,
  TimelineTimedCard,
  TimelineTimedCardHeader,
  TimelineTimedCardTime,
  TimelineMoreBadgeBtn,
} from 'components/calendar/Calendar.styles';
import {
  START_HOUR,
  HOUR_HEIGHT,
  TIMELINE_HEIGHT,
  HOURS,
  isAllDayEvent,
  getClickHourTimeByY,
  buildTimedLayouts,
  getScheduleStatus,
  getScheduleStyle,
} from 'utils/CalendarUtils';
import { EventMoreLayer } from 'components/calendar/EventMoreLayer';
import { useTimelineDnd } from './hooks/useTimelineDnd';
import { useTranslation } from 'react-i18next';

interface WeekViewProps {
  currentDate: Dayjs;
  schedules: PrjSchedule[];
  selectedCategories: Set<string>;
  isExpanded?: boolean;
  onSelectSchedule: (schedule: PrjSchedule) => void;
  onAddScheduleOnDate: (dateStr: string, timeStr?: string) => void;
  onShowMoreSchedules?: (dateStr: string, daySchedules: PrjSchedule[]) => void;
  onMoveScheduleDate: (
    schdId: string,
    targetDateStr: string,
    targetTime?: string,
    isAllDay?: boolean
  ) => void;
}

export const WeekView: React.FC<WeekViewProps> = ({
  currentDate,
  schedules,
  selectedCategories,
  isExpanded = false,
  onSelectSchedule,
  onAddScheduleOnDate,
  onShowMoreSchedules,
  onMoveScheduleDate,
}) => {
  const { t } = useTranslation();

  const WEEK_DAYS = [
    String(t('day.label.일', '일')),
    String(t('day.label.월', '월')),
    String(t('day.label.화', '화')),
    String(t('day.label.수', '수')),
    String(t('day.label.목', '목')),
    String(t('day.label.금', '금')),
    String(t('day.label.토', '토')),
  ];
  const startOfWeek = useMemo(() => currentDate.startOf('week'), [currentDate]);
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, 'day')),
    [startOfWeek]
  );

  const {
    handleDragStart,
    handleDragEnd,
    handleTimelineDragOver,
    handleTimelineDragLeave,
    handleTimelineDrop,
    handleAllDayDragOver,
    handleAllDayDragLeave,
    handleAllDayDrop,
    getTimelineIndicator,
    isAllDayDragOver,
  } = useTimelineDnd({ schedules, onMoveScheduleDate });

  const weekScheduleData = useMemo(() => {
    return weekDays.map((day, dayIndex) => {
      const dayStr = day.format('YYYY-MM-DD');
      const isToday = day.isSame(dayjs(), 'day');

      const daySchedules = schedules.filter((sch) => {
        const start = dayjs(sch.planStDtm).format('YYYY-MM-DD');
        const end = dayjs(sch.planEndDtm).format('YYYY-MM-DD');
        return dayStr >= start && dayStr <= end;
      });

      const allDayEvents = daySchedules.filter((sch) => isAllDayEvent(sch));
      const {
        items: timedLayouts,
        moreBadges,
        hasMore,
        maxColsForDay,
      } = buildTimedLayouts(schedules, day, {
        maxCols: 3,
        isExpanded,
      });

      return {
        day,
        dayStr,
        isToday,
        dayIndex,
        daySchedules,
        allDayEvents,
        timedLayouts,
        moreBadges,
        hasMore: Boolean(hasMore),
        maxColsForDay: maxColsForDay || 1,
      };
    });
  }, [schedules, weekDays, isExpanded]);

  // 주 캘린더 요일 너비 동적 계산 (펼치기 시 더보기 버튼이 있는 날짜만 컬럼 너비 확장)
  const { gridTemplateColumns, totalMinWidth } = useMemo(() => {
    let minWTotal = 88;
    const colDefs = weekScheduleData.map((item) => {
      if (isExpanded && item.hasMore) {
        const dayMinW = Math.max(140, item.maxColsForDay * 65);
        const frRatio = Math.max(1, item.maxColsForDay / 3).toFixed(2);
        minWTotal += dayMinW;
        return `minmax(${dayMinW}px, ${frRatio}fr)`;
      }
      minWTotal += 140;
      return 'minmax(140px, 1fr)';
    });
    return {
      gridTemplateColumns: `88px ${colDefs.join(' ')}`,
      totalMinWidth: Math.max(1120, minWTotal),
    };
  }, [weekScheduleData, isExpanded]);

  const [moreLayerState, setMoreLayerState] = useState<{
    open: boolean;
    anchorEl: HTMLElement | null;
    dateStr: string;
    schedules: PrjSchedule[];
  }>({
    open: false,
    anchorEl: null,
    dateStr: '',
    schedules: [],
  });

  const renderAllDayChip = (sch: PrjSchedule, dayStr: string) => {
    const category = sch.wktTpCd || 'ETC';
    const isDimmed = !selectedCategories.has(category);
    const status = getScheduleStatus(sch, dayStr);
    const style = getScheduleStyle(status, sch.wktTpCd, isDimmed);
    const displayTitle = sch.schdDisplayTitle || sch.schdNm || '일정';
    const showProcAlert = sch.schdProcYn === 'Y';

    return (
      <TimelineAllDayChip
        key={sch.schdId}
        title={`${displayTitle} (${dayjs(sch.planStDtm).format('MM-DD HH:mm')} ~ ${dayjs(
          sch.planEndDtm
        ).format('MM-DD HH:mm')}) - 드래그하여 날짜 이동 가능`}
        draggable={sch.schdStatCd !== 'CPLT'}
        onDragStart={(e) => handleDragStart(e, sch, dayStr, true)}
        onDragEnd={handleDragEnd}
        onClick={(e) => {
          e.stopPropagation();
          onSelectSchedule(sch);
        }}
        bg={style.bg}
        borderWidth={style.borderWidth}
        borderColor={style.border}
        borderLeft={style.borderLeft}
        textColor={style.text}
        opacity={style.opacity}
        lineThrough={style.lineThrough}
      >
        {showProcAlert && <IncompleteAlertBadge title="진행 중">!</IncompleteAlertBadge>}
        <CalEventTitleText>{displayTitle}</CalEventTitleText>
        {status === 'delayed' && <DelayedTagBadge>{t('com.label.지연', '지연')}</DelayedTagBadge>}
      </TimelineAllDayChip>
    );
  };

  return (
    <CalBoard minWidth={totalMinWidth}>
      <TimelineGridContainer minWidth={totalMinWidth} gridTemplateColumns={gridTemplateColumns}>
        {/* 헤더 행 */}
        <TimelineHeaderCol>{t('com.label.시간', '시간')}</TimelineHeaderCol>
        {weekScheduleData.map((item) => (
          <TimelineDayHeaderCell
            key={`day-header-${item.dayStr}`}
            isToday={item.isToday}
            onClick={() => onAddScheduleOnDate(item.dayStr)}
          >
            <TimelineDayNameText dayIndex={item.dayIndex}>
              {WEEK_DAYS[item.dayIndex]}
            </TimelineDayNameText>
            <TimelineDayDateText isToday={item.isToday}>
              {item.day.format('M/D')}
            </TimelineDayDateText>
          </TimelineDayHeaderCell>
        ))}

        {/* All day 영역 (다일 기간 일정) */}
        <TimelineAllDayLabel>All day</TimelineAllDayLabel>
        {weekScheduleData.map((item) => (
          <TimelineAllDayContent
            key={`${item.dayStr}-all`}
            isDragOver={isAllDayDragOver(item.dayStr)}
            onClick={() => onAddScheduleOnDate(item.dayStr)}
            onDragOver={(e) => handleAllDayDragOver(e, item.dayStr)}
            onDragLeave={(e) => handleAllDayDragLeave(e, item.dayStr)}
            onDrop={(e) => handleAllDayDrop(e, item.dayStr)}
          >
            {item.allDayEvents.map((sch) => renderAllDayChip(sch, item.dayStr))}
          </TimelineAllDayContent>
        ))}

        {/* 시간 축 */}
        <TimelineTimeAxisCol>
          <TimelineTimeAxisGrid height={TIMELINE_HEIGHT} rowCount={HOURS.length}>
            {HOURS.map((hour) => (
              <TimelineHourLabelCell key={`time-${hour}`} isFirst={hour === START_HOUR}>
                {`${String(hour).padStart(2, '0')}:00`}
              </TimelineHourLabelCell>
            ))}
          </TimelineTimeAxisGrid>
        </TimelineTimeAxisCol>

        {/* 각 요일별 타임라인 당일 시간 일정 영역 */}
        {weekScheduleData.map((item) => {
          const indicator = getTimelineIndicator(item.dayStr);

          return (
            <TimelineDayColumnArea
              key={`${item.dayStr}-timeline`}
              height={TIMELINE_HEIGHT}
              hourHeight={HOUR_HEIGHT}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const timeStr = getClickHourTimeByY(e.clientY, rect);
                onAddScheduleOnDate(item.dayStr, timeStr);
              }}
              onDragOver={(e) => handleTimelineDragOver(e, item.dayStr)}
              onDragLeave={(e) => handleTimelineDragLeave(e, item.dayStr)}
              onDrop={(e) => handleTimelineDrop(e, item.dayStr)}
            >
              {/* 드래그 오버 시 해당 요일 변경될 영역에 표시되는 점선 안내 영역 (Placeholder Indicator) */}
              {indicator && (
                <TimelineDragIndicatorBox
                  topPx={indicator.topPx}
                  heightPx={indicator.heightPx}
                  leftPx={3}
                  rightPx={3}
                >
                  <TimelineDragIndicatorBadge>🕒 {indicator.timeLabel}</TimelineDragIndicatorBadge>
                </TimelineDragIndicatorBox>
              )}

              {/* 3개 컬럼까지의 일정들 */}
              {item.timedLayouts.map((layoutItem) => {
                const category = layoutItem.sch.wktTpCd || 'ETC';
                const isDimmed = !selectedCategories.has(category);
                const status = getScheduleStatus(layoutItem.sch, item.dayStr);
                const style = getScheduleStyle(status, layoutItem.sch.wktTpCd, isDimmed);
                const displayTitle =
                  layoutItem.sch.schdDisplayTitle || layoutItem.sch.schdNm || '일정';
                const showProcAlert = layoutItem.sch.schdProcYn === 'Y';
                const showTimeDetail = layoutItem.heightPx >= 40;

                return (
                  <TimelineTimedCard
                    key={`${layoutItem.sch.schdId}-${layoutItem.startMin}-${layoutItem.col}`}
                    title={`${displayTitle} (${dayjs(layoutItem.sch.planStDtm).format(
                      'HH:mm'
                    )} ~ ${dayjs(layoutItem.sch.planEndDtm).format('HH:mm')})`}
                    draggable={layoutItem.sch.schdStatCd !== 'CPLT'}
                    onDragStart={(e) => handleDragStart(e, layoutItem.sch, item.dayStr, false)}
                    onDragEnd={handleDragEnd}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectSchedule(layoutItem.sch);
                    }}
                    topPx={layoutItem.topPx}
                    heightPx={layoutItem.heightPx}
                    leftPct={layoutItem.leftPct}
                    widthPct={layoutItem.widthPct}
                    bg={style.bg}
                    borderWidth={style.borderWidth}
                    borderColor={style.border}
                    borderLeft={style.borderLeft}
                    textColor={style.text}
                    opacity={style.opacity}
                    fontSize={layoutItem.totalCols > 2 ? 10 : 11}
                  >
                    <TimelineTimedCardHeader lineThrough={style.lineThrough}>
                      {showProcAlert && (
                        <IncompleteAlertBadge title="진행 중">!</IncompleteAlertBadge>
                      )}
                      <CalEventTitleText>{displayTitle}</CalEventTitleText>
                      {status === 'delayed' && (
                        <DelayedTagBadge>{t('com.label.지연', '지연')}</DelayedTagBadge>
                      )}
                    </TimelineTimedCardHeader>
                    {showTimeDetail && (
                      <TimelineTimedCardTime>
                        {dayjs(layoutItem.sch.planStDtm).format('HH:mm')} ~{' '}
                        {dayjs(layoutItem.sch.planEndDtm).format('HH:mm')}
                      </TimelineTimedCardTime>
                    )}
                  </TimelineTimedCard>
                );
              })}

              {/* 3개 초과 일정에 대한 More 배지/버튼 */}
              {item.moreBadges.map((badge) => {
                const clusterSchedules =
                  badge.allClusterSchedules && badge.allClusterSchedules.length > 0
                    ? badge.allClusterSchedules
                    : badge.hiddenSchedules;
                return (
                  <TimelineMoreBadgeBtn
                    key={badge.id}
                    topPx={badge.topPx}
                    leftPct={badge.leftPct}
                    widthPct={badge.widthPct}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMoreLayerState({
                        open: true,
                        anchorEl: e.currentTarget,
                        dateStr: item.dayStr,
                        schedules: clusterSchedules,
                      });
                      if (onShowMoreSchedules) {
                        onShowMoreSchedules(item.dayStr, clusterSchedules);
                      }
                    }}
                  >
                    +{badge.hiddenCount} more
                  </TimelineMoreBadgeBtn>
                );
              })}
            </TimelineDayColumnArea>
          );
        })}
      </TimelineGridContainer>

      {/* 4번 요구사항: 화면 밖으로 잘리지 않는 더보기 레이어 */}
      <EventMoreLayer
        open={moreLayerState.open}
        anchorEl={moreLayerState.anchorEl}
        dateStr={moreLayerState.dateStr}
        schedules={moreLayerState.schedules}
        selectedCategories={selectedCategories}
        onClose={() => setMoreLayerState((prev) => ({ ...prev, open: false, anchorEl: null }))}
        onSelectSchedule={onSelectSchedule}
      />
    </CalBoard>
  );
};
